"use client";

import Link from "next/link";
import Image from "next/image";
import ProductsMenu from "./components/ProductsMenu";
import NavLink from "./components/NavLink";
import { useState, useEffect, useCallback, memo } from "react";
import { type Route } from 'next';
// Comment out these imports until we can fix path issues
// import BackToTop from "./components/BackToTop";
// import CookieConsent from "./components/CookieConsent";
import StickyWhatsAppButton from '../components/StickyWhatsAppButton';

// Memoize the header and footer components to prevent unnecessary re-renders
const Header = memo(function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Add scroll event listener to track when user has scrolled with throttling
  useEffect(() => {
    const handleScroll = () => {
      // Only update state if the scrolled state actually changes
      const isScrolled = window.scrollY > 50;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };

    // Throttled event listener using requestAnimationFrame
    let ticking = false;
    const throttledScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    // Add event listener with passive option for better performance
    window.addEventListener("scroll", throttledScroll, { passive: true });
    
    // Clean up
    return () => {
      window.removeEventListener("scroll", throttledScroll);
    };
  }, [scrolled]); // Add scrolled as dependency to optimize performance

  // Optimized handler to close menu
  const closeMenu = useCallback(() => {
    setMobileMenuOpen(false);
  }, []);

  return (
    <header 
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled 
          ? "bg-white shadow-md border-b border-slate-200" 
          : "bg-white border-b border-slate-100"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20 py-3">
          {/* Logo - fixed dimensions to prevent layout shift */}
          <div className="flex-shrink-0 py-2">
            <Link href="/" className="flex items-center">
              <div className="relative w-40 h-16">
                <Image 
                  src="/logo.png" 
                  alt="EMS Pulse Logo"
                  fill
                  style={{ objectFit: "contain" }}
                  priority
                  sizes="160px"
                  quality={90}
                />
              </div>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-6">
            <NavLink href="/">Home</NavLink>
            <ProductsMenu />
            <NavLink href="/why-ems-pulse">Why EMS Pulse</NavLink>
            <NavLink href="/technology-safety">Technology</NavLink>
            <NavLink href="/support-training">Support</NavLink>
            <NavLink href="/roi">ROI</NavLink>
            <NavLink href="/contact">Contact</NavLink>
          </nav>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center">
            <Link
              href="/contact"
              className="inline-flex items-center bg-[#287e6f] text-white font-medium px-5 py-2 rounded-lg hover:bg-[#1e665a] transition-all duration-300 shadow-md hover:shadow-lg hover:translate-y-[-2px]"
            >
              Request a Quote
            </Link>
          </div>

          {/* Mobile menu button - optimized for touch */}
          <div className="md:hidden">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 rounded-md text-[#212529] hover:text-[#32a895] focus:outline-none transition-colors"
              aria-expanded={mobileMenuOpen}
              aria-label="Toggle menu"
              style={{ touchAction: 'manipulation' }}
            >
              <span className="sr-only">Open main menu</span>
              {mobileMenuOpen ? (
                <svg
                  className="h-6 w-6"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              ) : (
                <svg
                  className="h-6 w-6"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* Mobile menu - use CSS transitions instead of JS animation for better performance */}
        <div 
          className={`md:hidden py-4 px-3 bg-white border-t border-slate-100 shadow-lg transition-all duration-300 ease-in-out ${
            mobileMenuOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0 overflow-hidden pointer-events-none'
          }`}
        >
          <nav className="space-y-2">
            <Link
              href="/"
              className="block px-4 py-3 rounded-md text-base font-medium text-[#1a2942] hover:text-[#38b5a0] hover:bg-slate-50 transition-all"
              onClick={closeMenu}
            >
              Home
            </Link>

            <Link
              href="/i-motion-system"
              className="block px-4 py-3 rounded-md text-base font-medium text-[#1a2942] hover:text-[#38b5a0] hover:bg-slate-50 transition-all"
              onClick={closeMenu}
            >
              I-motion System
            </Link>

            <Link
              href="/i-model-system"
              className="block px-4 py-3 rounded-md text-base font-medium text-[#1a2942] hover:text-[#38b5a0] hover:bg-slate-50 transition-all"
              onClick={closeMenu}
            >
              i-model System
            </Link>

            <Link
              href="/why-ems-pulse"
              className="block px-4 py-3 rounded-md text-base font-medium text-[#1a2942] hover:text-[#38b5a0] hover:bg-slate-50 transition-all"
              onClick={closeMenu}
            >
              Why EMS Pulse
            </Link>

            <Link
              href="/technology-safety"
              className="block px-4 py-3 rounded-md text-base font-medium text-[#1a2942] hover:text-[#38b5a0] hover:bg-slate-50 transition-all"
              onClick={closeMenu}
            >
              Technology
            </Link>

            <Link
              href="/support-training"
              className="block px-4 py-3 rounded-md text-base font-medium text-[#1a2942] hover:text-[#38b5a0] hover:bg-slate-50 transition-all"
              onClick={closeMenu}
            >
              Support
            </Link>

            <Link
              href="/roi"
              className="block px-4 py-3 rounded-md text-base font-medium text-[#1a2942] hover:text-[#38b5a0] hover:bg-slate-50 transition-all"
              onClick={closeMenu}
            >
              ROI
            </Link>

            <Link
              href="/contact"
              className="block px-4 py-3 rounded-md text-base font-medium text-[#1a2942] hover:text-[#38b5a0] hover:bg-slate-50 transition-all"
              onClick={closeMenu}
            >
              Contact
            </Link>

            <div className="pt-2 mt-2 border-t border-slate-100">
              <Link
                href="/contact"
                className="block mx-4 mt-2 py-3 px-4 bg-[#287e6f] text-white text-center font-medium rounded-lg hover:bg-[#1e665a] transition-all shadow-md"
                onClick={closeMenu}
              >
                Request a Quote
              </Link>
            </div>
          </nav>
        </div>
      </div>
    </header>
  );
});

// Memoize the footer component to prevent unnecessary re-renders
const Footer = memo(function Footer() {
  return (
    <footer className="bg-[#1a2942] text-white pt-16 pb-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 md:gap-12 mb-16">
          <div className="col-span-1 md:col-span-1">
            <div className="mb-6">
              <Link href="/" className="inline-block">
                <div className="relative w-48 h-16">
                  <Image 
                    src="/logo-white.png" 
                    alt="EMS Pulse Logo" 
                    fill
                    style={{ objectFit: "contain" }}
                    sizes="(max-width: 768px) 160px, 192px"
                  />
                </div>
              </Link>
            </div>
            <p className="text-slate-300 mb-6 text-sm">
              The exclusive UK distributor for I-motion EMS Technology, providing advanced EMS training systems to the fitness and wellness industry.
            </p>
            <div className="flex space-x-4">
              <a href="https://www.facebook.com/emspulse" target="_blank" rel="noopener noreferrer" className="text-slate-300 hover:text-white transition-colors">
                <span className="sr-only">Facebook</span>
                <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path fillRule="evenodd" d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" clipRule="evenodd" />
                </svg>
              </a>
              <a href="https://www.instagram.com/emspulse" target="_blank" rel="noopener noreferrer" className="text-slate-300 hover:text-white transition-colors">
                <span className="sr-only">Instagram</span>
                <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path fillRule="evenodd" d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z" clipRule="evenodd" />
                </svg>
              </a>
              <a href="https://www.linkedin.com/company/emspulse" target="_blank" rel="noopener noreferrer" className="text-slate-300 hover:text-white transition-colors">
                <span className="sr-only">LinkedIn</span>
                <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                </svg>
              </a>
            </div>
          </div>

          <div className="col-span-1">
            <h3 className="text-lg font-semibold mb-4">EMS Systems</h3>
            <ul className="space-y-3">
              <li>
                <Link href="/i-motion-system" className="text-slate-300 hover:text-white transition-colors">I-motion System</Link>
              </li>
              <li>
                <Link href="/i-model-system" className="text-slate-300 hover:text-white transition-colors">i-model System</Link>
              </li>
              <li>
                <Link href="/technology-safety" className="text-slate-300 hover:text-white transition-colors">Technology & Safety</Link>
              </li>
              <li>
                <Link href="/certifications" className="text-slate-300 hover:text-white transition-colors">Certifications</Link>
              </li>
              <li>
                <Link href="/support-training" className="text-slate-300 hover:text-white transition-colors">Support & Training</Link>
              </li>
            </ul>
          </div>

          <div className="col-span-1">
            <h3 className="text-lg font-semibold mb-4">Company</h3>
            <ul className="space-y-3">
              <li>
                <Link href="/why-ems-pulse" className="text-slate-300 hover:text-white transition-colors">Why EMS Pulse</Link>
              </li>
              <li>
                <Link href="/roi" className="text-slate-300 hover:text-white transition-colors">ROI Calculator</Link>
              </li>
              <li>
                <Link href="/contact" className="text-slate-300 hover:text-white transition-colors">Contact Us</Link>
              </li>
            </ul>
          </div>

          <div className="col-span-1">
            <h3 className="text-lg font-semibold mb-4">Legal</h3>
            <ul className="space-y-3">
              <li>
                <Link href="/privacy-policy" className="text-slate-300 hover:text-white transition-colors">Privacy Policy</Link>
              </li>
              <li>
                <Link href="/terms-of-service" className="text-slate-300 hover:text-white transition-colors">Terms of Service</Link>
              </li>
              <li>
                <Link href="/cookie-policy" className="text-slate-300 hover:text-white transition-colors">Cookie Policy</Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-gray-700 pt-8">
          <p className="text-sm text-slate-400 text-center">
            &copy; {new Date().getFullYear()} EMS Pulse Ltd. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
});

export default function ClientLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  // Once-only effect to detect and set viewport height for mobile browsers
  useEffect(() => {
    // Fix for mobile 100vh issue
    const setVh = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };

    setVh();
    window.addEventListener('resize', setVh);

    return () => {
      window.removeEventListener('resize', setVh);
    };
  }, []);

  return (
    <>
      <div className="flex flex-col min-h-screen overflow-x-hidden">
        <Header />
        <main className="flex-grow">{children}</main>
        <Footer />
      </div>
      <StickyWhatsAppButton />
    </>
  );
}
